import { clsx } from 'clsx';
import { useContext } from 'react';
import { getTailwindColor } from '@/utils/colors';
import { BlockBase } from '@/queries/page';
import { getImageUrl } from '@/utils/images';
import { AppContext } from '@/contexts/app-context';
import { DynamicModuleGenerator } from '../../dynamic-module-generator/dynamic-module-generator';
import { VerticalWave, Wave } from './svg';

export type HeroProps = {
    backgroundColor: string;
    innerBlocks: BlockBase[];
    imageWaveType: 'half' | 'full';
    image: { src: string; width: number; height: number; __isimage: boolean };
    imagePosition?: 'left' | 'right';
    visibility?: 'both' | 'mobile' | 'desktop';
    linkColor?: {
        key: string;
        color: string;
    };
};

export const Hero = ({ backgroundColor, innerBlocks, imageWaveType, image, imagePosition = 'right', visibility = 'both', linkColor }: HeroProps) => {
    const { deviceType } = useContext(AppContext);

    const containerClass = clsx(getTailwindColor(backgroundColor, 'bg'), 'r-relative', {
        'r-hidden': (visibility === 'desktop' && deviceType === 'mobile') || (visibility === 'mobile' && deviceType === 'desktop'),
        [`link-color-${linkColor?.key?.replace('#', '')}`]: linkColor?.key,
    });

    return (
        <div data-component="hero" className={containerClass}>
            {linkColor?.key && (
                <style
                    type="text/css"
                    dangerouslySetInnerHTML={{
                        __html: `.link-color-${linkColor?.key?.replace('#', '')} a:not(.resurs-button) { color: ${linkColor?.color} !important; }`,
                    }}
                />
            )}
            <div className="r-container r-relative r-grid md:r-grid-cols-[50%,50%]">
                <div
                    className={clsx(
                        'r-px-4 r-py-6 [&>h1]:r-text-[28px] [&>h1]:r-leading-[40px]',
                        'md:r-py-12 md:[&>h1]:r-text-[54px] md:[&>h1]:r-leading-[75px]',
                        {
                            'md:r-col-start-2 md:r-px-24': imagePosition === 'left',
                            'md:r-px-8': imagePosition === 'right',
                        }
                    )}
                >
                    <DynamicModuleGenerator blocks={innerBlocks} />
                </div>

                {/* Mobile Image */}
                <div
                    className={clsx('r-relative r-order-first r-h-[220px] r-bg-cover r-bg-no-repeat md:r-hidden', {
                        'r-rounded-bl-[140px]': imageWaveType === 'half',
                    })}
                    style={{
                        backgroundImage: `url(${getImageUrl(image?.src, '1920xAUTO')})`,
                    }}
                >
                    {imageWaveType === 'full' && (
                        <div className="r-absolute r-bottom-0 r-w-full">
                            <Wave fill={backgroundColor} />
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop Image - positioned outside container */}
            <div
                className={clsx('r-absolute r-bottom-0 r-top-0 r-hidden r-w-[50%] r-bg-cover r-bg-center md:r-block', {
                    'r-rounded-bl-[360px]': imageWaveType === 'half' && imagePosition === 'right',
                    'r-rounded-br-[360px]': imageWaveType === 'half' && imagePosition === 'left',
                    'r-left-0': imagePosition === 'left',
                    'r-right-0': imagePosition === 'right',
                })}
                style={{
                    backgroundImage: `url(${getImageUrl(image?.src, '1920xAUTO')})`,
                }}
            >
                {imageWaveType === 'full' && (
                    <div
                        className={clsx('r-absolute r-bottom-0 r-top-0 r-h-full', {
                            'r-right-[-16px] r-rotate-180': imagePosition === 'left',
                            'r-left-[-16px]': imagePosition === 'right',
                        })}
                    >
                        <VerticalWave fill={backgroundColor} />
                    </div>
                )}
            </div>
        </div>
    );
};
